<script setup lang="ts">
import VbList from "@/components/crud/VbList.vue"
import VbListSearchFilterButton from "@/components/crud/VbListLoginSearchFilterButton.vue"
import VbListSearch from "@/components/crud/VbListSearch.vue"
import VbListToolbar from "@/components/crud/VbListToolbar.vue"
import { useIssueMessageCount } from "@/composables/useIssueMessageCount"
import { useLoginSearchFilter } from "@/composables/useLoginSearchFilter"
import { createModuleConfiguration } from "@/pages/Tasks/Customer/config"
import FilterProjectsDropdown from "@/pages/Tasks/Customer/FilterProjectsDropdown.vue"
import type { IssueListItem } from "@/pages/Tasks/Issue/config"
import FilterDoneIssuesDropdown from "@/pages/Tasks/Issue/FilterDoneIssuesDropdown.vue"
import FilterOnlyMessagesButton from "@/pages/Tasks/Issue/FilterOnlyMessagesButton.vue"
import FilterMyOrganizationsDropdown from "@/pages/Tasks/Issue/FilterOrganizationsDropdown.vue"
import { type ListState } from "@/vf"
import { ref } from "vue"
import { useRouter } from "vue-router"

const props = withDefaults(
    defineProps<{
        allowNew?: boolean
        onlyMessages?: boolean
    }>(),
    { allowNew: true, onlyMessages: false },
)

const router = useRouter()
const filterOnlyMessages = ref(props.onlyMessages)
const config = createModuleConfiguration("tasks/issue", filterOnlyMessages)
config.list.newButton = () => props.allowNew
if (props.onlyMessages) {
    config.list.requestParams.value = {
        ...(config.list.requestParams.value ?? {}),
        onlyMessages: "1",
    }
}
let listState: ListState

const listId = config.list?.id()
const loginSearchFilter = useLoginSearchFilter()
const { loadUnreadMessages } = useIssueMessageCount()

async function onLoaded({ listState: newState }: { listState: ListState }) {
    listState = newState
    if (router.currentRoute.value.query.issue) {
        listState.columnClicked(
            { id: router.currentRoute.value.query.issue as string },
            { name: "virtual", title: "virtual", type: "string" },
        )
        // after issue query param is handled, remove it from the URL so it doesn't toggle the issue on next refresh
        // of the list
        router.replace({ query: { ...router.currentRoute.value.query, issue: undefined } })
    }
    loginSearchFilter.init(listId, listState)

    // Load unread messages for all displayed issues after the list has loaded
    if (listState.list && listState.list.length > 0) {
        await loadUnreadMessages(listState.list as IssueListItem[])
    }
}
</script>

<template>
    <VbList :config="config" @loaded="onLoaded" :mobile-list="true">
        <template #toolbar="{ refresh }">
            <VbListToolbar></VbListToolbar>
            <FilterOnlyMessagesButton
                v-bind="{ config }"
                @toggle="filterOnlyMessages = $event"
                class="card-toolbar__item"
            ></FilterOnlyMessagesButton>
            <FilterDoneIssuesDropdown v-bind="{ config }" class="card-toolbar__item"></FilterDoneIssuesDropdown>
            <FilterProjectsDropdown
                v-bind="{ admin: false, config }"
                class="card-toolbar__item"
            ></FilterProjectsDropdown>
            <FilterMyOrganizationsDropdown
                :customer-view="true"
                class="card-toolbar__item"
            ></FilterMyOrganizationsDropdown>
            <VbListSearch @search="refresh()" class="card-toolbar__item"></VbListSearch>
            <div>
                <VbListSearchFilterButton
                    v-bind="{ filter }"
                    v-for="filter in loginSearchFilter?.getFilters(listId)"
                    :key="filter.id"
                    :class="{ 'btn-primary-dark': loginSearchFilter?.isFilterActive(filter) }"
                    class="card-toolbar__item"
                    @click="loginSearchFilter?.loadFilter(filter)"
                ></VbListSearchFilterButton>
            </div>
        </template>
    </VbList>
</template>
